import React, { useState, useEffect } from 'react';
import {
  useProductBranding,
  useUpdateProductBranding,
} from '../../../../../shared/hooks/useProductBranding';
import {
  RELIX_DEFAULT_ACCENT,
  isValidHex,
  normalizeHex,
} from '../../../../../features/reports/export/colorUtils';

export interface BrandColorPickerProps {
  productId: string | null;
}

export const BrandColorPicker: React.FC<BrandColorPickerProps> = ({ productId }) => {
  const branding = useProductBranding(productId);
  const update = useUpdateProductBranding();
  const stored = branding.data?.brandColor ?? null;

  // Locally edited value so users can type freely without spamming PATCH on
  // every keystroke. We commit on blur or on the colour-picker change event.
  const [local, setLocal] = useState<string>(stored ?? '');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLocal(stored ?? '');
  }, [stored]);

  const commit = (raw: string) => {
    if (!productId) return;
    const trimmed = raw.trim();
    if (trimmed === '') {
      setError(null);
      update.mutate({ productId, brandColor: null });
      return;
    }
    if (!isValidHex(trimmed)) {
      setError('Use a hex color like #7C5CFF.');
      return;
    }
    setError(null);
    update.mutate({ productId, brandColor: normalizeHex(trimmed) });
  };

  const handleNativeColor = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setLocal(v.toUpperCase());
    commit(v);
  };

  return (
    <div className="rb-brand-color">
      <label className="rb-brand-color__row">
        <span
          className="rb-brand-color__swatch"
          style={{ background: local || RELIX_DEFAULT_ACCENT }}
        />
        <input
          type="text"
          className="rb-brand-color__input"
          value={local}
          placeholder="#7C5CFF"
          onChange={(e) => {
            setLocal(e.target.value);
            setError(null);
          }}
          onBlur={(e) => commit(e.target.value)}
          disabled={!productId || update.isPending}
        />
        <span className="rb-brand-color__sep" aria-hidden="true" />
        <input
          type="color"
          className="rb-brand-color__native"
          value={isValidHex(local) ? (normalizeHex(local) as string) : RELIX_DEFAULT_ACCENT}
          onChange={handleNativeColor}
          aria-label="Pick brand color"
        />
        <button
          type="button"
          className="rb-brand-color__reset"
          onClick={() => commit('')}
          disabled={!productId || update.isPending}
        >
          Use Relix default
        </button>
      </label>
      {error ? <p className="rb-brand-color__error">{error}</p> : null}
    </div>
  );
};
